import clsx from "clsx";
import { useLayoutEffect, useRef } from "react";

import type { VolumeConverterData, VolumeUnit } from "../../types";
import type { RebateTierTranslatedContent } from "../../useRebateTierTranslatedContent";
import type { VolumeConverterSelection } from "./useVolumeConverterSelection";

import { Button } from "../../../Button";
import { CheckIcon, WarningIllustration } from "../../../Icons";
import { Typo } from "../../../Typo";
import { formatLotNotional, volumeUnitKey } from "../../formatVolume";
import { SheetNotice } from "../SheetNotice";
import { SaveDefaultCheckbox } from "./SaveDefaultCheckbox";
import { useVolumeConverterSelection } from "./useVolumeConverterSelection";

export type VolumeConverterCardProps = {
  value: VolumeUnit;
  converter: VolumeConverterData;
  content: RebateTierTranslatedContent;
  onApply: (unit: VolumeUnit, saveAsDefault: boolean) => void;
  showHeading?: boolean;
  selection?: VolumeConverterSelection;
  showFooter?: boolean;
  scrollList?: boolean;
};

const NOTICE_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

type UnitOptionProps = {
  label: string;
  description: string;
  isSelected: boolean;
  badge?: string;
  onSelect: () => void;
  "data-testid": string;
};

const UnitOption = ({
  label,
  description,
  isSelected,
  badge,
  onSelect,
  "data-testid": testId,
}: UnitOptionProps) => (
  <button
    type="button"
    role="radio"
    aria-checked={isSelected}
    onClick={onSelect}
    data-testid={testId}
    className={clsx(
      "flex w-full items-center justify-between gap-3 rounded-lg p-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stroke-focused",
      isSelected && "bg-state-selected",
    )}
  >
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="flex items-center gap-2">
        <Typo
          element="span"
          type="bodyMD"
          textColor={isSelected ? "text-text-selected" : "text-text-primary"}
        >
          {label}
        </Typo>
        {badge && (
          <Typo
            element="span"
            type="labelXS"
            textColor="text-text-informative-bold"
            className="rounded-full bg-surface-accent-blue-subtler px-2 py-0.5"
          >
            {badge}
          </Typo>
        )}
      </span>
      <Typo element="span" type="bodyXS" textColor="text-text-secondary">
        {description}
      </Typo>
    </span>
    {isSelected && (
      <CheckIcon
        aria-hidden="true"
        className="h-5 w-5 shrink-0 [&_path]:stroke-stroke-selected"
      />
    )}
  </button>
);

const scrollContainerOf = (list: HTMLElement) => {
  for (let node: HTMLElement | null = list; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return null;
};

const useScrollCheckedOptionIntoViewOnOpen = () => {
  const listRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    // BottomSheet measures its header and footer after this mounts, so its scroll area only settles on the next frame.
    const frame = requestAnimationFrame(() => {
      const list = listRef.current;
      const checked = list?.querySelector<HTMLElement>('[aria-checked="true"]');
      const container = list && scrollContainerOf(list);
      if (!checked || !container) return;
      const top =
        checked.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop;
      if (
        top + checked.offsetHeight >
        container.scrollTop + container.clientHeight
      ) {
        container.scrollTop = top;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, []);
  return listRef;
};

export const VolumeConverterCard = ({
  value,
  converter,
  content,
  onApply,
  showHeading = true,
  selection,
  showFooter = true,
  scrollList = false,
}: VolumeConverterCardProps) => {
  const ownSelection = useVolumeConverterSelection(value);
  const { pending, setPending, saveAsDefault, setSaveAsDefault } =
    selection ?? ownSelection;

  const selected = volumeUnitKey(pending);
  const savedDefault = volumeUnitKey(converter.savedDefault);
  const defaultBadge = (key: string) =>
    key === savedDefault ? content.volumeConverterDefaultBadge : undefined;

  const listRef = useScrollCheckedOptionIntoViewOnOpen();

  const restoreFocusAfterRetry = useRef(false);
  useLayoutEffect(() => {
    if (converter.isError || !restoreFocusAfterRetry.current) return;
    restoreFocusAfterRetry.current = false;
    if (document.activeElement !== document.body) return;
    listRef.current
      ?.querySelector<HTMLElement>('[aria-checked="true"]')
      ?.focus();
  }, [converter.isError, listRef]);

  const retry = () => {
    restoreFocusAfterRetry.current = true;
    converter.refetch?.();
  };

  const heading = showHeading && (
    <Typo
      element="h3"
      type="headingMD"
      textColor="text-text-primary"
      className="pb-4"
    >
      {content.volumeConverterTitle}
    </Typo>
  );

  if (converter.isError) {
    return (
      <div className="flex flex-col">
        {heading}
        <div
          role="alert"
          data-testid="rebate_tier_volume_converter_error_message"
          className="flex flex-col items-center justify-center gap-4 p-8 text-center"
        >
          <WarningIllustration
            className="h-12 w-12 shrink-0"
            aria-hidden="true"
            focusable="false"
          />
          <Typo element="p" type="bodySM" textColor="text-text-secondary">
            {content.volumeConverterLoadError}
          </Typo>
          <Button
            variant="brand"
            size="md"
            isLoading={converter.isLoading}
            onClick={retry}
            data-testid="rebate_tier_volume_converter_error_refresh_button"
          >
            {content.errorStateRefresh}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx("flex flex-col", scrollList && "min-h-0")}>
      {heading}

      {/* p-1 fits the rows' 4px focus outline inside the scroll clip; no negative bottom margin, so the list never runs under the footer divider. */}
      <div
        ref={listRef}
        className={clsx(
          "relative flex flex-col gap-4",
          scrollList && "-mx-1 -mt-1 min-h-0 flex-1 overflow-y-auto p-1",
        )}
      >
        {converter.rateDate && (
          <SheetNotice
            message={content.volumeConverterNotice(
              new Date(converter.rateDate).toLocaleDateString(
                "en-GB",
                NOTICE_DATE_FORMAT,
              ),
            )}
          />
        )}

        <div role="radiogroup" aria-label={content.volumeConverterTitle}>
          <UnitOption
            label={content.volumeConverterUsd}
            description={content.volumeConverterUsdDescription}
            isSelected={selected === "usd"}
            badge={defaultBadge("usd")}
            onSelect={() => setPending({ kind: "usd" })}
            data-testid="rebate_tier_volume_unit_usd_button"
          />

          {converter.options.map((option) => (
            <UnitOption
              key={option.symbol}
              label={option.symbol}
              description={content.volumeConverterLotEquivalent(
                formatLotNotional(option.perLotUsd),
              )}
              isSelected={selected === option.symbol}
              badge={defaultBadge(option.symbol)}
              onSelect={() => setPending({ kind: "instrument", ...option })}
              data-testid={`rebate_tier_volume_unit_${option.symbol.toLowerCase()}_button`}
            />
          ))}
        </div>
      </div>

      {showFooter && (
        <div className="flex shrink-0 items-center justify-between border-t border-stroke-primary pt-2">
          <SaveDefaultCheckbox
            id="volume-converter-save-default"
            checked={saveAsDefault}
            onChange={setSaveAsDefault}
            label={content.saveAsDefault}
            labelType="labelXS"
            data-testid="rebate_tier_volume_converter_card_save_default_checkbox"
          />
          <Button
            variant="brand"
            size="sm"
            onClick={() => onApply(pending, saveAsDefault)}
            data-testid="rebate_tier_volume_converter_card_apply_button"
          >
            {content.apply}
          </Button>
        </div>
      )}
    </div>
  );
};
